import type {
  SourcesList,
  SyncItemStatus,
  SyncSource,
  SyncState,
  SyncTile,
} from "@trip/contracts/bridge";
import {
  Check,
  ChevronDown,
  ChevronRight,
  CloudUpload,
  ImagePlus,
  MemoryStick,
  Play,
  Smartphone,
  Wifi,
  WifiOff,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { get, put, type Trip } from "../lib/api.ts";
import { nativeBridge, type Bridge } from "../lib/bridge.ts";
import { Link, tripPath } from "../lib/router.tsx";
import { formatBytes } from "../lib/transfer.ts";
import {
  Banner,
  Card,
  EmptyState,
  ICON,
  Pill,
  ProgressBar,
  StatusPill,
  type LadderState,
} from "../ui/index.ts";
import { SourceIcon } from "./SourcesPage.tsx";

/** Native item states on the protection ladder (same words everywhere, DESIGN.md §8). */
const LADDER_OF: Partial<Record<SyncItemStatus, LadderState | "FAILED">> = {
  QUEUED: "QUEUED",
  HASHING: "QUEUED",
  UPLOADING: "UPLOADING",
  VERIFYING: "AWAITING_VERIFICATION",
  VERIFIED: "VERIFIED",
  FAILED: "FAILED",
};
/** Tiles shown in a collapsed group before the "+N" tile. */
const PREVIEW = 7;
type Route = "WIFI" | "CELLULAR" | "NONE" | "OTHER";
type StorageUse = { usedBytes: string; quotaBytes: string | null };

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/**
 * D. Media Sync, the traveller's home: what each shared folder has from this trip, every tile
 * pre-checked (checkmark top right) and uncheckable, the total, the cloud space, and one big Sync
 * button that waits for Wi-Fi. Only media taken inside the trip window is ever listed; anything
 * else goes through "Upload media", the person's own choice.
 */
export function SyncPage({
  trip,
  bridge = nativeBridge(),
}: {
  trip: Trip;
  bridge?: Bridge | null;
}) {
  const [list, setList] = useState<SourcesList | null>(null);
  const [state, setState] = useState<SyncState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [route, setRoute] = useState<Route | null>(null);
  const [storage, setStorage] = useState<StorageUse | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [opened, setOpened] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    if (!bridge) return;
    setError(null);
    try {
      const l = await bridge.call("sources.list", { tripId: trip.id });
      setList(l);
      if (!l.blocked) setState(l.sync);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [bridge, trip.id]);

  useEffect(() => {
    void load();
    if (!bridge) return;
    const off = bridge.on("sync.changed", (s) => {
      if (s.tripId && s.tripId !== trip.id) return;
      setState(s);
      // A finished run moves its files out of the grid: list again from the phone.
      if (s.phase === "DONE") void load();
    });
    const offNet = bridge.on("network.changed", (n) => setRoute(n.route));
    // Unknown (an older app) never blocks the button; the native uploader enforces Wi-Fi anyway.
    bridge.call("device.network", {}).then(
      (n) => setRoute(n.route),
      () => undefined,
    );
    return () => {
      off();
      offNet();
    };
  }, [load, bridge, trip.id]);

  useEffect(() => {
    if (!bridge) return;
    get<{ items: StorageUse[] }>(`/v1/trips/${trip.id}/storage-nodes`).then(
      (r) => setStorage(r.items[0] ?? null),
      () => setStorage(null),
    );
  }, [bridge, trip.id, state?.phase]);

  const tiles = useMemo(() => (list && !list.blocked ? list.items : []), [list]);
  const sources: SyncSource[] = list && !list.blocked ? list.sources : [];
  const checked = tiles.filter((t) => t.checked);
  const running = state?.phase === "SYNCING" || state?.phase === "SCANNING";
  const statusOf = useMemo(
    () => new Map((state?.items ?? []).map((i) => [i.id, i.status] as const)),
    [state],
  );
  const bySource = useMemo(() => {
    const m = new Map<string, SyncTile[]>();
    for (const t of tiles) m.set(t.sourceId, [...(m.get(t.sourceId) ?? []), t]);
    return m;
  }, [tiles]);

  if (!bridge) {
    return (
      <EmptyState
        icon={<Smartphone size={24} strokeWidth={1.5} />}
        title="Syncing runs in the Android app"
        body="Open Trip Media on your phone to upload this trip’s photos and videos. Here you can see who has synced under People & Sync."
      />
    );
  }

  async function toggle(t: SyncTile) {
    const next = !t.checked;
    setList((l) =>
      l && !l.blocked
        ? { ...l, items: l.items.map((x) => (x.id === t.id ? { ...x, checked: next } : x)) }
        : l,
    );
    try {
      await bridge!.call("sources.toggle", { tripId: trip.id, id: t.id, checked: next });
    } catch (e) {
      setError((e as Error).message);
      void load();
    }
  }

  async function start() {
    setBusy(true);
    setNote(null);
    try {
      setState(await bridge!.call("sync.start", { tripId: trip.id }));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function manual() {
    setNote(null);
    try {
      const r = await bridge!.call("sources.pickManual", { tripId: trip.id });
      if (r.sync) setState(r.sync);
      if (r.picked > 0)
        setNote(`${r.picked} ${r.picked === 1 ? "file" : "files"} added by you; uploading now.`);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function shareFootage() {
    try {
      const c = await get<{ version: number }>(`/v1/trips/${trip.id}/consents/MEDIA`);
      await put(`/v1/trips/${trip.id}/consents/MEDIA`, {
        decision: "GRANTED",
        baseVersion: c.version,
      });
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  if (list?.blocked) {
    const why = {
      NOT_STARTED: {
        title: "The trip has not started",
        body: "Sync opens when the trip admin starts the trip.",
        action: undefined,
      },
      NO_CONSENT: {
        title: "Share your footage with this trip",
        body: "Nothing leaves your phone until you choose to share it. Only media taken during the trip is offered.",
        action: { label: "Share my footage", onClick: () => void shareFootage() },
      },
      NO_PERMISSION: {
        title: "Allow access to photos and videos",
        body: "The app needs to read your photos and videos to find what you took during the trip. It never changes or deletes them.",
        action: { label: "Allow access", onClick: () => void load() },
      },
    }[list.blocked];
    return (
      <EmptyState
        title={why.title}
        body={why.body}
        {...(why.action ? { action: why.action } : {})}
      />
    );
  }

  const flip = (set: Set<string>, id: string) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  };
  const total = checked.reduce((a, t) => a + t.sizeBytes, 0);
  const done = state?.phase === "DONE";
  const offWifi = route !== null && route !== "WIFI";
  const card = list && !list.blocked ? list.card : null;

  const tileView = (t: SyncTile) => {
    const st = statusOf.get(t.id);
    const ladder = st ? LADDER_OF[st] : undefined;
    const what = `${t.kind === "VIDEO" ? "video" : "photo"} from ${new Date(t.capturedAt).toLocaleString()}`;
    return (
      <li key={t.id} className={`tile${t.checked ? " checked" : ""}`}>
        <label>
          <img
            src={`/thumbs/${t.id}.jpg`}
            alt={what[0]!.toUpperCase() + what.slice(1)}
            loading="lazy"
          />
          <input
            type="checkbox"
            checked={t.checked}
            disabled={running}
            onChange={() => void toggle(t)}
            aria-label={`Include ${what}`}
          />
          <span className="tile-check" aria-hidden="true">
            {t.checked && <Check size={16} strokeWidth={3} />}
          </span>
          {t.kind === "VIDEO" && (
            <span className="tile-play" aria-hidden="true">
              <Play size={14} strokeWidth={2} fill="currentColor" />
            </span>
          )}
        </label>
        {ladder && (
          <div className="tile-status">
            <StatusPill state={ladder} />
          </div>
        )}
        {st === "PAUSED" && <div className="tile-status pill wait">Paused</div>}
        {st === "DUPLICATE" && <div className="tile-status pill">Already in the trip</div>}
        {st === "WAITING_SOURCE" && <div className="tile-status pill wait">Waiting for card</div>}
      </li>
    );
  };

  const groups = sources.filter((s) => bySource.has(s.id) || (s.removable && !s.connected));
  return (
    <div className="stack sync-home">
      {error && (
        <Banner
          tone="bad"
          message={error}
          action={{ label: "Retry", onClick: () => void load() }}
        />
      )}

      <Card className="sync-conn">
        <p className={`conn-row${offWifi ? " off" : ""}`}>
          {offWifi ? (
            <WifiOff {...ICON} aria-hidden="true" />
          ) : (
            <Wifi {...ICON} aria-hidden="true" />
          )}
          {offWifi ? "Not on Wi-Fi" : "Wi-Fi connected"}
        </p>
        <p className={`conn-row${card?.mounted ? "" : " off"}`}>
          <MemoryStick {...ICON} aria-hidden="true" />
          {card?.mounted
            ? `${card.name ?? "Memory card"} connected`
            : "No memory card connected · phone media still syncs"}
        </p>
      </Card>

      <Card
        title="Ready to sync"
        actions={list ? <Pill tone="good">Scan complete</Pill> : <Pill tone="wait">Scanning…</Pill>}
      >
        {list && tiles.length === 0 && groups.length === 0 ? (
          <EmptyState
            title="Nothing new from this trip"
            body="Everything you took during the trip in your shared folders is uploaded or being checked."
          />
        ) : (
          groups.map((s) => {
            const own = bySource.get(s.id) ?? [];
            const open = !collapsed.has(s.id) && own.length > 0;
            const all = opened.has(s.id);
            const shown = all || own.length <= PREVIEW + 1 ? own : own.slice(0, PREVIEW);
            const bytes = own.reduce((a, t) => a + t.sizeBytes, 0);
            return (
              <div className="source-group" key={s.id}>
                <button
                  type="button"
                  className="source-head"
                  aria-expanded={open}
                  disabled={own.length === 0}
                  onClick={() => setCollapsed((c) => flip(c, s.id))}
                >
                  <span className="row-icon" aria-hidden="true">
                    <SourceIcon kind={s.kind} name={s.name} />
                  </span>
                  <span className="row-main">
                    <span className="row-title">{s.name}</span>
                    <span className="row-detail">
                      {own.length > 0 ? plural(own.length, "new file") : "Not connected"}
                    </span>
                  </span>
                  {own.length > 0 && <span className="source-size">{formatBytes(bytes)}</span>}
                  {own.length > 0 &&
                    (open ? (
                      <ChevronDown {...ICON} aria-hidden="true" />
                    ) : (
                      <ChevronRight {...ICON} aria-hidden="true" />
                    ))}
                </button>
                {open && (
                  <ul className="tile-grid plain" aria-label={`Media from ${s.name}`}>
                    {shown.map(tileView)}
                    {shown.length < own.length && (
                      <li className="tile more">
                        <button
                          type="button"
                          onClick={() => setOpened((o) => flip(o, s.id))}
                          aria-label={`Show ${own.length - shown.length} more from ${s.name}`}
                        >
                          +{own.length - shown.length}
                        </button>
                      </li>
                    )}
                  </ul>
                )}
              </div>
            );
          })
        )}
        {tiles.length > 0 && (
          <p className="sync-total">
            {plural(checked.length, "new file")} · {formatBytes(total)}
          </p>
        )}
      </Card>

      <Link to={tripPath(trip.id, "sources")} className="text-link">
        Manage source permissions
        <ChevronRight {...ICON} aria-hidden="true" />
      </Link>

      {storage && <StorageCard use={storage} adding={total} />}

      <div className="sync-action">
        {running ? (
          <>
            <ProgressBar
              value={state && state.items.length > 0 ? state.verified / state.items.length : null}
              label="Uploaded and verified"
              tone="good"
            />
            <p className="small muted" aria-live="polite" style={{ margin: 0 }}>
              {state?.verified ?? 0} of {state?.items.length ?? 0} uploaded and verified
            </p>
            <button
              className="button big"
              type="button"
              onClick={() => void bridge!.call("sync.stop", {}).then(setState)}
            >
              Stop
            </button>
          </>
        ) : (
          <button
            className="button primary big"
            type="button"
            disabled={
              busy ||
              offWifi ||
              (checked.length === 0 && !(list && !list.blocked && list.resumable > 0))
            }
            onClick={start}
          >
            <CloudUpload size={22} strokeWidth={1.5} aria-hidden="true" />
            Sync
          </button>
        )}
        {offWifi && !running && (
          <p role="status" className="small" style={{ margin: 0 }}>
            Connect to Wi-Fi to sync. Sync never uses mobile data.
          </p>
        )}
        {done && state?.message && (
          <p role="status" className="small" style={{ margin: 0 }}>
            {state.message}
          </p>
        )}
        {note && (
          <p role="status" className="small muted" style={{ margin: 0 }}>
            {note}
          </p>
        )}
        <p className="small muted sync-fine">Wi-Fi only · Pauses if Wi-Fi disconnects</p>
        <p className="small muted sync-fine">
          Uploads are verified, then checked for backup protection. Sync never deletes your
          originals.
        </p>
      </div>

      {list && !list.blocked && list.unsupported > 0 && (
        <p className="muted small">
          {list.unsupported} {list.unsupported === 1 ? "file" : "files"} in a format that is not
          supported yet.
        </p>
      )}
      <div>
        <button className="button" type="button" onClick={manual} disabled={running}>
          <ImagePlus {...ICON} aria-hidden="true" />
          Upload media
        </button>
        <p className="muted small">
          Add photos or videos from outside the trip dates yourself; they are never added
          automatically.
        </p>
      </div>
    </div>
  );
}

/** Cloud space: with a quota, used of quota and what is left after this sync; never an invented limit. */
function StorageCard({ use, adding }: { use: StorageUse; adding: number }) {
  const used = Number(use.usedBytes);
  const quota = use.quotaBytes === null ? null : Number(use.quotaBytes);
  const left = quota === null ? null : quota - used - adding;
  return (
    <Card className="storage-use">
      <p className="storage-line">
        <strong>Cloud storage</strong>
        <span className="muted small">
          {quota === null
            ? `${formatBytes(used)} used`
            : `${formatBytes(used)} of ${formatBytes(quota)} used`}
        </span>
      </p>
      {quota !== null && <ProgressBar value={used / quota} label="Cloud storage used" />}
      <p className="muted small" style={{ margin: 0 }}>
        {left === null
          ? `This sync adds ${formatBytes(adding)}`
          : left >= 0
            ? `${formatBytes(left)} available after this sync`
            : `This sync needs ${formatBytes(-left)} more than the space left`}
      </p>
    </Card>
  );
}
